import { ClassListType } from "utils/interfaces";
import AssignClassCard from "./cards/AssignClassCard";
import { useContext, useEffect, useState } from "react";
import { singlify } from "utils/utils";
import EmptyViewPopUp from "../EmptyViewPopUp";
import { getCollectionRequest } from "services/apiRequests";
import { LoadingDots } from "utils/loadingAnimation";
import SuccessPopUpMessage from "../SuccessPopUpMessage";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { logoutFunction } from "services/authRequests";

interface ClassesCardsListProps {
  selectedClasses: string[];
  closeOnceClickOnCard: boolean;
  onDoneCloseClick: () => void;
  showDoneButton: boolean;
  selectAction?: (
    classItem: ClassListType,
    action: "add" | "remove",
  ) => Promise<void>;
}

const ClassesCardsList: React.FC<ClassesCardsListProps> = ({
  selectedClasses,
  closeOnceClickOnCard,
  onDoneCloseClick,
  showDoneButton,
  selectAction,
}) => {
  // States definition
  const [classData, setClassData] = useState<ClassListType[]>([]);
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentClassName, setCurrentClassName] = useState("");

  // Get the values from the context
  const { classesTerminology } = usePlatform();
  const { contextSchoolByRole } = useContext(MainContext);

  /**
   * Function to load the data
   */
  const getData = async () => {
    // First of all, start the animation
    setIsLoadingData(true);

    // Check if there is contextSchoolByRole (for edition)
    if (!(contextSchoolByRole && contextSchoolByRole._id)) {
      return [];
    }

    // Get request
    const response = await getCollectionRequest(
      `/api/classes?schoolId=${contextSchoolByRole._id}`,
    );

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    return data;
  };

  /**
   * Definition of the Add or remove action for internal
   * display purposes
   * @param id of the selected id in the pop up menu
   * @param action "add" or "remove" "selectedData._id" from "id"
   */
  const _selectActionWrapper = async (
    classItem: ClassListType,
    action: "add" | "remove",
  ) => {
    // Execute the defined action
    if (selectAction) selectAction(classItem, action);

    // Extra animation logic
    if (action === "add") {
      // Close the panel
      if (closeOnceClickOnCard) onDoneCloseClick();
      // Or send the signal to show the pop up message
      else {
        setShowSuccessPopUp(true);
        setCurrentClassName(classItem.name);
      }
    }
    // Or delete from it
    else if (action === "remove") {
      // And force the close of the success message
      setShowSuccessPopUp(false);
    }
  };

  /**
   * Load class data when init
   */
  useEffect(() => {
    // Get the data
    getData().then((data) => {
      // Set the data
      setClassData(data);

      // Stop the animation
      setIsLoadingData(false);
    });
  }, []);

  /**
   * Timer for the warning pop up message
   */
  useEffect(() => {
    if (showSuccessPopUp) {
      // Starting the timer
      const timer = setTimeout(() => {
        // Once finished, close the pop up
        if (setShowSuccessPopUp && setCurrentClassName) {
          setShowSuccessPopUp(false);
          setCurrentClassName("");
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessPopUp]);

  return (
    <>
      <div className="w-full max-h-[70vh] grow shrink justify-start items-start gap-[5px] inline-flex overflow-y-auto pop-up-scrollbar">
        <div className="w-full h-fit self-stretch flex-col justify-start items-start gap-4 inline-flex">
          {classData.length > 0 ? (
            classData.map((item: ClassListType, index: number) => (
              <AssignClassCard
                key={index}
                classItem={item}
                selectedClassDataIds={selectedClasses}
                selectAction={_selectActionWrapper}
              />
            ))
          ) : !isLoadingData ? (
            <EmptyViewPopUp />
          ) : (
            <div className="w-full h-[50vh] flex justify-center items-center">
              <LoadingDots bgColor="bg-neutral-300" />
            </div>
          )}
        </div>
        <SuccessPopUpMessage
          name={currentClassName}
          nameType={singlify(classesTerminology)}
          showSuccessPopUp={showSuccessPopUp}
          setShowSuccessPopUp={setShowSuccessPopUp}
          dismissAction={() => {}}
          className={`absolute ${
            showDoneButton ? "bottom-16" : "-bottom-3"
          } w-full h-12 p-3 bg-success-500 rounded-2xl justify-start items-center gap-3 inline-flex`}
        />
      </div>
      {showDoneButton && (
        <div
          className="group self-stretch h-11 px-5 py-3 rounded border border-secondary-500 hover:bg-secondary-600 ease-in-out duration-150 inline-flex justify-center items-center cursor-pointer"
          onClick={onDoneCloseClick}
        >
          <div className="justify-start text-secondary-500 group-hover:text-neutral-25 ease-in-out duration-150 text-lg font-semibold font-sans leading-snug select-none">
            Done
          </div>
        </div>
      )}
    </>
  );
};

export default ClassesCardsList;
